import { useEffect, useState, useRef, useImperativeHandle, forwardRef } from "react";
import StateOutput from "./output/StateOutput";
import '../styles/output.css'
import FullAudio from "./output/FullAudio";
import TranscripcioOutput from "./output/TranscripcioOutput";
import AudioParts from "./output/AudioParts";

const Output = forwardRef(({ fileName, state, setState, processor, isModelLoaded, isAudioLoaded, numSpeakers, isPremium, onLinesChange }, ref) => {
    const [lines, setLines] = useState([]);
    const [audioParts, setAudioParts] = useState([]);
    const [fullAudio, setFullAudio] = useState(null);
    const [queue, setQueue] = useState([]);
    const [toBeFinished, setToBeFinished] = useState(false);
    const [speakers, setSpeakers] = useState([]);
    const [lastLineTimestamp, setLastLineTimestamp] = useState(Date.now());
    const audioRef = useRef(null);

    // Expose lines through ref
    useImperativeHandle(ref, () => ({
        getLines: () => lines
    }));

    // Update parent component when lines change
    useEffect(() => {
        if (onLinesChange) {
            onLinesChange(lines);
        }
    }, [lines, onLinesChange]);

    useEffect(() => {
        const intervalId = setInterval(() => {
            if (queue.length > 0) {
                setLines(prev => {
                    const newLines = [...prev, queue[0]];
                    return newLines;
                });
                setQueue(prev => prev.slice(1));
                setLastLineTimestamp(Date.now());
            }
        }, 75);

        return () => clearInterval(intervalId);
    }, [queue]);

    useEffect(() => {
        if (toBeFinished && queue.length === 0) {
            setState(prev => Math.max(prev, 7));
        }
    }, [toBeFinished, queue.length]);

    const addNewLine = (line) => {
        // Add to queue
        setQueue(prev => [...prev, line]);
    }

    const addNewAudioPart = (audioPart) => {
        setAudioParts(prev => [...prev, audioPart]);
    }

    const addFullAudio = (audio) => {
        setFullAudio(audio);
    }

    const changeSpeakers = (speakers) => {
        setSpeakers(speakers);
    }

    const changeState = (newState) => {
        if (newState === 7) {
            setToBeFinished(true);
        } else {
            setState(prev => Math.max(prev, newState));
        }
    }

    useEffect(() => {
        processor?.setOutput({
            lines: addNewLine,
            audioParts: addNewAudioPart,
            fullAudio: addFullAudio,
            speakers: changeSpeakers,
            changeState,
        });
    }, [processor]);

    useEffect(() => {
        if (!isModelLoaded && !isAudioLoaded) {
            setState(0);
        }

        if (isModelLoaded && !isAudioLoaded) {
            setState(1);
        }

        if (!isModelLoaded && isAudioLoaded) {
            setState(2);
        }

        if (isModelLoaded && isAudioLoaded) {
            setState(3);
        }
    }, [
        isModelLoaded,
        isAudioLoaded,
    ]);

    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (state > 3 && state < 7) {
                e.preventDefault();
                e.returnValue = '';
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [state]);

    useEffect(() => {
        // If in a processing state (e.g. 5 "Àudio processat. Comença la transcripció..."),
        // start a 5-minute timeout. If the state hasn't advanced, set an error state.
        if (state === 5) {
            const timeoutId = setTimeout(() => {
                console.error("Transcription process timed out after 5 minutes. Setting error state.");
                setState(9); // Set error state
            }, 300000); // 5 minutes in milliseconds

            return () => clearTimeout(timeoutId);
        }
    }, [state]);

    // Add timeout for state 6 (transcription in progress)
    useEffect(() => {
        if (state === 6) {
            const checkInactivity = () => {
                const now = Date.now();
                if (now - lastLineTimestamp > 300000) { // 5 minutes in milliseconds
                    console.error("No new lines received for 5 minutes. Setting error state.");
                    setState(9); // Set error state
                }
            };

            const intervalId = setInterval(checkInactivity, 10000); // Check every 10 seconds

            return () => clearInterval(intervalId);
        }
    }, [state, lastLineTimestamp]);

    return (
        <div>
            <StateOutput state={state} />
            <FullAudio fullAudio={fullAudio} ref={audioRef} />
            {/* <AudioParts processor={processor} audioParts={audioParts} state={state} /> */}

            <TranscripcioOutput fileName={fileName} lines={lines} state={state} speakers={speakers} numSpeakers={numSpeakers} isPremium={isPremium} audioRef={audioRef} />
        </div>
    );
});

export default Output;